import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { DynamicTextConfig } from '@shared/components/dynamic-text/dynamic-text.types';
import { Dynamic } from '@shared/registry/dynamic';

import { type DetailCardConfig } from './detail-card.types';

@Component({
  selector: 'app-detail-card',
  templateUrl: './detail-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailCard {
  readonly config = input.required<DetailCardConfig>();

  protected readonly isHighlighted = computed(() => this.config().variant === 'highlighted');
  protected readonly hasListLayout = computed(() => this.config().hasListLayout ?? false);

  protected readonly containerBackground = computed(() => {
    if (this.isHighlighted()) {
      return 'linear-gradient(135deg, var(--gradients-tertiary-start), var(--gradients-tertiary-middle), var(--gradients-tertiary-stop))';
    }
    return 'var(--detailcard-idle-background)';
  });

  // Pre-computed props objects to avoid creating new objects during rendering
  protected readonly chipProps = computed(() => {
    const chip = this.config().chip;
    if (!chip) return null;
    return { config: { ...chip, variant: chip.variant ?? ('subtle' as const) } };
  });

  protected readonly titleProps = computed(() => {
    const variant = this.config().variant;
    const titles = this.config().titles ?? [];
    return titles.map((text) => ({
      config: {
        text,
        size: 'xl' as const,
        fontWeight: 'semibold' as const,
        whiteSpace: this.config().whiteSpace,
        cssVarPrefix: `detailcard-${variant}-title`,
      } as DynamicTextConfig,
    }));
  });

  protected readonly subtextProps = computed(() => {
    const variant = this.config().variant;
    const subtexts = this.config().subtexts ?? [];
    return subtexts.map((text) => ({
      config: {
        text,
        size: 'base' as const,
        fontWeight: 'semibold' as const,
        whiteSpace: this.config().whiteSpace,
        cssVarPrefix: `detailcard-${variant}-subtext`,
      } as DynamicTextConfig,
    }));
  });

  protected readonly descriptionProps = computed(() => {
    const desc = this.config().description;
    if (!desc) return null;
    return {
      config: {
        text: desc,
        size: 'sm' as const,
        fontWeight: 'bold' as const,
        whiteSpace: this.config().whiteSpace,
        cssVarPrefix: `detailcard-${this.config().variant}-description`,
      } as DynamicTextConfig,
    };
  });

  protected readonly fieldProps = computed(() => {
    const variant = this.config().variant;
    const fields = this.config().fields ?? [];
    return fields.map((field) => ({
      titleProps: {
        config: {
          text: field.title,
          size: 'sm' as const,
          fontWeight: 'bold' as const,
          whiteSpace: this.config().whiteSpace,
          cssVarPrefix: `detailcard-${variant}-field-title`,
        } as DynamicTextConfig,
      },
      contentProps: {
        config: {
          text: field.content,
          size: 'base' as const,
          fontWeight: 'semibold' as const,
          whiteSpace: this.config().whiteSpace,
          cssVarPrefix: `detailcard-${variant}-field-content`,
        } as DynamicTextConfig,
      },
    }));
  });
}
